import { useState, useEffect } from "react";
import { Card, CardBody, CardHeader, Button, Input, Chip, Tooltip, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from "@heroui/react";
import { Plus, Upload, CheckCircle, Trash, PencilSimple, Download, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "react-toastify";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useSession } from "@clerk/clerk-react";

interface HealthIndex {
  id: number;
  year: number;
  month: number;
  value: number;
  source?: string;
  baseYear: string;
  createdAt: string;
  updatedAt: string;
}

interface Statistics {
  totalEntries: number;
  earliestYear: number | null;
  latestYear: number | null;
  missingInCurrentYear: number;
  baseYears: string[];
}

const MONTH_NAMES = [
  "Januari", "Februari", "Maart", "April", "Mei", "Juni",
  "Juli", "Augustus", "September", "Oktober", "November", "December"
];

const HealthIndexManagementTab = () => {
  const { session } = useSession();
  const [indices, setIndices] = useState<HealthIndex[]>([]);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filterYear, setFilterYear] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");

  // Add/Edit modal state
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<HealthIndex | null>(null);
  const [formData, setFormData] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    value: "",
    source: "Statbel",
    baseYear: "2013",
  });

  // Bulk import modal state
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [bulkData, setBulkData] = useState("");
  const [skipExisting, setSkipExisting] = useState(true);

  // Load data on mount
  useEffect(() => {
    fetchIndices();
    fetchStatistics();
  }, [session]);

  // Fetch all health indices
  const fetchIndices = async (year?: string) => {
    if (!session) return;

    try {
      const url = year
        ? `${API_URL}/rental/admin/health-index?year=${year}`
        : `${API_URL}/rental/admin/health-index`;

      const response = await fetchWithAuth(url, { method: "GET" }, session);

      if (!response.ok) throw new Error("Failed to fetch indices");

      const data = await response.json();
      setIndices(data);
    } catch (error) {
      console.error("Error fetching health indices:", error);
      toast.error("Fout bij ophalen van gezondheidsindex data");
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch statistics
  const fetchStatistics = async () => {
    if (!session) return;

    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/admin/health-index/statistics`,
        { method: "GET" },
        session
      );

      if (!response.ok) throw new Error("Failed to fetch statistics");

      const data = await response.json();
      setStatistics(data);
    } catch (error) {
      console.error("Error fetching statistics:", error);
    }
  };

  // Handle filter change
  const handleFilterChange = (year: string) => {
    setFilterYear(year);
    fetchIndices(year || undefined);
  };

  // Open add modal
  const handleAddNew = () => {
    setEditingIndex(null);
    setFormData({
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      value: "",
      source: "Statbel",
      baseYear: "2013",
    });
    setIsAddModalOpen(true);
  };

  // Open edit modal
  const handleEdit = (index: HealthIndex) => {
    setEditingIndex(index);
    setFormData({
      year: index.year,
      month: index.month,
      value: index.value.toString(),
      source: index.source || "Statbel",
      baseYear: index.baseYear || "2013",
    });
    setIsAddModalOpen(true);
  };

  // Save (create or update)
  const handleSave = async () => {
    if (!session) return;

    const value = parseFloat(formData.value);
    if (isNaN(value) || value <= 0) {
      toast.error("Ongeldige waarde. Moet groter zijn dan 0.");
      return;
    }

    try {
      if (editingIndex) {
        // Update existing
        const response = await fetchWithAuth(
          `${API_URL}/rental/admin/health-index/${editingIndex.id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              value,
              source: formData.source,
              baseYear: formData.baseYear,
            }),
          },
          session
        );

        if (!response.ok) throw new Error("Failed to update");
        toast.success("Gezondheidsindex bijgewerkt");
      } else {
        // Create new
        const response = await fetchWithAuth(
          `${API_URL}/rental/admin/health-index`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              year: formData.year,
              month: formData.month,
              value,
              source: formData.source,
              baseYear: formData.baseYear,
            }),
          },
          session
        );

        if (!response.ok) throw new Error("Failed to create");
        toast.success("Gezondheidsindex toegevoegd");
      }

      setIsAddModalOpen(false);
      fetchIndices(filterYear || undefined);
      fetchStatistics();
    } catch (error) {
      console.error("Error saving health index:", error);
      toast.error("Fout bij opslaan");
    }
  };

  // Delete
  const handleDelete = async (id: number) => {
    if (!session) return;
    if (!confirm("Weet je zeker dat je deze index wilt verwijderen?")) return;

    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/admin/health-index/${id}`,
        { method: "DELETE" },
        session
      );

      if (!response.ok) throw new Error("Failed to delete");

      toast.success("Gezondheidsindex verwijderd");
      fetchIndices(filterYear || undefined);
      fetchStatistics();
    } catch (error) {
      console.error("Error deleting health index:", error);
      toast.error("Fout bij verwijderen");
    }
  };

  // Bulk import
  const handleBulkImport = async () => {
    if (!session) return;

    try {
      // Parse CSV format: Year,Month,Value
      const lines = bulkData.trim().split("\n");
      const indices = lines
        .filter(line => line.trim() && !line.startsWith("Year")) // Skip header
        .map(line => {
          const [year, month, value] = line.split(",").map(s => s.trim());
          return {
            year: parseInt(year),
            month: parseInt(month),
            value: parseFloat(value),
            source: "Bulk Import",
          };
        });

      if (indices.length === 0) {
        toast.error("Geen geldige data gevonden");
        return;
      }

      const response = await fetchWithAuth(
        `${API_URL}/rental/admin/health-index/bulk`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            indices,
            skipExisting,
          }),
        },
        session
      );

      if (!response.ok) throw new Error("Bulk import failed");

      const result = await response.json();
      toast.success(
        `Import voltooid! Geïmporteerd: ${result.imported}, Overgeslagen: ${result.skipped}, Fouten: ${result.errors.length}`
      );

      if (result.errors.length > 0) {
        console.error("Import errors:", result.errors);
      }

      setIsBulkModalOpen(false);
      setBulkData("");
      fetchIndices(filterYear || undefined);
      fetchStatistics();
    } catch (error) {
      console.error("Error during bulk import:", error);
      toast.error("Fout bij bulk import");
    }
  };

  // Export to CSV
  const handleExport = () => {
    const csv = [
      "Year,Month,Value,Source,BaseYear",
      ...indices.map(i =>
        `${i.year},${i.month},${i.value},"${i.source || ""}","${i.baseYear}"`
      ),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `health-indices-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Filter indices by search term
  const filteredIndices = indices.filter(index => {
    if (!searchTerm) return true;
    const monthName = MONTH_NAMES[index.month - 1].toLowerCase();
    const search = searchTerm.toLowerCase();
    return (
      index.year.toString().includes(search) ||
      monthName.includes(search) ||
      index.value.toString().includes(search) ||
      index.source?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="flex flex-col gap-6">
      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card className="border border-neutral-secondaryFine">
            <CardBody className="p-4">
              <div className="text-sm text-neutral-secondary-mid mb-1">Totaal entries</div>
              <div className="text-2xl font-bold text-neutral-primary">
                {statistics.totalEntries}
              </div>
            </CardBody>
          </Card>

          <Card className="border border-neutral-secondaryFine">
            <CardBody className="p-4">
              <div className="text-sm text-neutral-secondary-mid mb-1">Vroegste jaar</div>
              <div className="text-2xl font-bold text-neutral-primary">
                {statistics.earliestYear || "-"}
              </div>
            </CardBody>
          </Card>

          <Card className="border border-neutral-secondaryFine">
            <CardBody className="p-4">
              <div className="text-sm text-neutral-secondary-mid mb-1">Laatste jaar</div>
              <div className="text-2xl font-bold text-neutral-primary">
                {statistics.latestYear || "-"}
              </div>
            </CardBody>
          </Card>

          <Card className="border border-neutral-secondaryFine">
            <CardBody className="p-4">
              <div className="text-sm text-neutral-secondary-mid mb-1">Basisjaren</div>
              <div className="text-lg font-bold text-neutral-primary">
                {statistics.baseYears.length > 0 ? statistics.baseYears.join(", ") : "-"}
              </div>
            </CardBody>
          </Card>

          <Card className="border border-neutral-secondaryFine">
            <CardBody className="p-4">
              <div className="text-sm text-neutral-secondary-mid mb-1">Ontbrekend dit jaar</div>
              <div className={`text-2xl font-bold ${statistics.missingInCurrentYear > 0 ? "text-brick-primary" : "text-status-good"}`}>
                {statistics.missingInCurrentYear}
              </div>
            </CardBody>
          </Card>
        </div>
      )}

      {/* Info Card */}
      <Card className="border-2 border-moss-primary/30 bg-moss-secondaryFine/30">
        <CardBody className="p-4">
          <div className="flex items-start gap-3">
            <CheckCircle size={24} weight="fill" className="text-moss-primary mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold text-moss-primary mb-1">
                Gezondheidsindex Beheer
              </div>
              <div className="text-sm text-neutral-secondary-mid">
                Beheer maandelijkse gezondheidsindex waarden voor Belgische huurprijsindexatie.
                Data wordt gebruikt voor de interne indexatie calculator.{" "}
                <strong>Bron: Statbel (FOD Economie)</strong>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Action Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1">
          <Input
            placeholder="Zoek op jaar, maand, waarde..."
            value={searchTerm}
            onValueChange={setSearchTerm}
            startContent={<MagnifyingGlass size={18} className="text-neutral-secondary-mid" />}
            classNames={{
              base: "max-w-xs",
              input: "text-sm",
            }}
            size="sm"
          />

          <Input
            type="number"
            placeholder="Filter op jaar..."
            value={filterYear}
            onValueChange={handleFilterChange}
            classNames={{
              base: "max-w-[150px]",
              input: "text-sm",
            }}
            size="sm"
          />
        </div>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="flat"
            startContent={<Download size={16} />}
            onPress={handleExport}
          >
            Exporteer CSV
          </Button>

          <Button
            size="sm"
            variant="flat"
            startContent={<Upload size={16} />}
            onPress={() => setIsBulkModalOpen(true)}
          >
            Bulk Import
          </Button>

          <Button
            size="sm"
            color="primary"
            startContent={<Plus size={16} weight="bold" />}
            onPress={handleAddNew}
          >
            Nieuwe Index
          </Button>
        </div>
      </div>

      {/* Table */}
      <Card>
        <CardHeader className="bg-neutral-secondaryFine px-4 py-3">
          <div className="text-base font-semibold">Gezondheidsindex Waarden</div>
        </CardHeader>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-neutral-secondary-mid">Laden...</div>
            </div>
          ) : filteredIndices.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-neutral-secondary-mid">Geen data gevonden</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-neutral-secondaryFine border-b border-neutral-secondaryFine">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-neutral-secondary-mid">
                      Jaar
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-neutral-secondary-mid">
                      Maand
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-neutral-secondary-mid">
                      Waarde
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-neutral-secondary-mid">
                      Bron
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-neutral-secondary-mid">
                      Basisjaar
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-neutral-secondary-mid">
                      Acties
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredIndices.map((index, i) => (
                    <tr
                      key={index.id}
                      className={`border-b border-neutral-secondaryFine hover:bg-neutral-secondaryFine/50 ${
                        i % 2 === 0 ? "bg-white" : "bg-neutral-secondaryFine/20"
                      }`}
                    >
                      <td className="px-4 py-3 text-sm font-medium">{index.year}</td>
                      <td className="px-4 py-3 text-sm">
                        {MONTH_NAMES[index.month - 1]}
                      </td>
                      <td className="px-4 py-3 text-sm font-mono font-semibold">
                        {index.value.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-sm text-neutral-secondary-mid">
                        {index.source || "-"}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <Chip size="sm" className="bg-moss-secondaryFine text-moss-primary border border-moss-primary/30">
                          {index.baseYear}
                        </Chip>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center justify-end gap-1">
                          <Tooltip content="Bewerken">
                            <Button
                              isIconOnly
                              size="sm"
                              variant="light"
                              onPress={() => handleEdit(index)}
                            >
                              <PencilSimple size={16} />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Verwijderen">
                            <Button
                              isIconOnly
                              size="sm"
                              variant="light"
                              color="danger"
                              onPress={() => handleDelete(index.id)}
                            >
                              <Trash size={16} />
                            </Button>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Add/Edit Modal */}
      <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} size="lg">
        <ModalContent>
          <ModalHeader>
            {editingIndex ? "Gezondheidsindex bewerken" : "Nieuwe gezondheidsindex toevoegen"}
          </ModalHeader>
          <ModalBody>
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Jaar"
                  type="number"
                  value={formData.year.toString()}
                  onValueChange={(v) => setFormData({ ...formData, year: parseInt(v) || 2024 })}
                  isDisabled={!!editingIndex}
                />
                <Input
                  label="Maand (1-12)"
                  type="number"
                  value={formData.month.toString()}
                  onValueChange={(v) => setFormData({ ...formData, month: parseInt(v) || 1 })}
                  isDisabled={!!editingIndex}
                />
              </div>

              <Input
                label="Waarde"
                type="number"
                step="0.01"
                value={formData.value}
                onValueChange={(v) => setFormData({ ...formData, value: v })}
                description="Basis 2013 = 100"
              />

              <Input
                label="Bron"
                value={formData.source}
                onValueChange={(v) => setFormData({ ...formData, source: v })}
                placeholder="bijv. Statbel March 2025"
              />

              <Input
                label="Basisjaar"
                value={formData.baseYear}
                onValueChange={(v) => setFormData({ ...formData, baseYear: v })}
                description="bijv. 2013, 2004"
                placeholder="2013"
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={() => setIsAddModalOpen(false)}>
              Annuleren
            </Button>
            <Button color="primary" onPress={handleSave}>
              {editingIndex ? "Bijwerken" : "Toevoegen"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Bulk Import Modal */}
      <Modal isOpen={isBulkModalOpen} onClose={() => setIsBulkModalOpen(false)} size="2xl">
        <ModalContent>
          <ModalHeader>Bulk Import - CSV Formaat</ModalHeader>
          <ModalBody>
            <div className="flex flex-col gap-4">
              <div className="text-sm text-neutral-secondary-mid">
                <p className="mb-2">Plak CSV data in het formaat:</p>
                <code className="block bg-neutral-secondaryFine p-2 rounded-sm text-xs font-mono">
                  Year,Month,Value<br />
                  2025,1,134.28<br />
                  2025,2,135.79
                </code>
              </div>

              <textarea
                className="w-full h-64 p-3 border border-neutral-secondaryFine rounded-lg font-mono text-sm"
                value={bulkData}
                onChange={(e) => setBulkData(e.target.value)}
                placeholder="Year,Month,Value&#10;2025,1,134.28&#10;2025,2,135.79"
              />

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={skipExisting}
                  onChange={(e) => setSkipExisting(e.target.checked)}
                  className="w-4 h-4"
                />
                <label className="text-sm">Sla bestaande waarden over (aanbevolen)</label>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={() => setIsBulkModalOpen(false)}>
              Annuleren
            </Button>
            <Button color="primary" onPress={handleBulkImport}>
              Importeren
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default HealthIndexManagementTab;
